import { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Input,
  Select,
  Space,
  Typography,
  Tooltip,
  Button,
  DatePicker,
  Row,
  Col,
  Statistic,
  Modal,
  Rate,
  Descriptions,
  message,
  Popconfirm,
} from 'antd';
import {
  SearchOutlined,
  EyeOutlined,
  ReloadOutlined,
  BarChartOutlined,
  DownloadOutlined,
  DeleteOutlined,
  CalendarOutlined,
  StarOutlined,
  ContactsOutlined,
} from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import {
  SimpleFeedback,
  DemoFeedbackListParams,
  DemoFeedbackStats,
  demoFeedbackApi,
} from '../lib/demoFeedbackApi';

const { Title, Text, Paragraph } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const DemoFeedbackPage: React.FC = () => {
  const [feedbacks, setFeedbacks] = useState<SimpleFeedback[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [searchText, setSearchText] = useState('');
  const [selectedRating, setSelectedRating] = useState<number | undefined>();
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([null, null]);
  const [statistics, setStatistics] = useState<DemoFeedbackStats | null>(null);
  const [selectedFeedback, setSelectedFeedback] = useState<SimpleFeedback | null>(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);

  useEffect(() => {
    fetchFeedbacks();
    fetchStatistics();
  }, [currentPage, pageSize, searchText, selectedRating, dateRange]);

  // Mock data for demonstration when API is not available  
  const mockFeedbacks: SimpleFeedback[] = [
    {
      id: '1',
      name: 'John Smith',
      email: 'john.smith@company.com',
      phone: '+1-555-0123',
      organization: 'Tech Solutions Inc',
      rating: 5,
      comments: 'Very impressed with the mobile solutions demo. Would like to explore integration options.',
      createdAt: '2024-01-01T12:00:00Z',
      updatedAt: '2024-01-01T12:00:00Z',
    },
    {
      id: '2',
      name: 'Sarah Johnson',
      email: 'sarah.j@manufacturing.com',
      phone: '+1-555-0124',
      organization: 'Manufacturing Corp',
      rating: 4,
      comments: 'Need more information about migration timeline and costs.',
      createdAt: '2024-01-02T12:00:00Z',
      updatedAt: '2024-01-02T12:00:00Z',
    },
    {
      id: '3',
      name: 'Mike Wilson',
      email: 'mike.wilson@enterprise.com',
      phone: '+1-555-0125',
      organization: 'Enterprise Systems',
      rating: 5,
      comments: 'Excellent presentation on support extension services.',
      createdAt: '2024-01-03T12:00:00Z',
      updatedAt: '2024-01-03T12:00:00Z',
    },
    {
      id: '4',
      name: 'Lisa Chen',
      email: 'lisa.chen@cloudtech.com',
      phone: '+1-555-0126',
      organization: 'CloudTech Solutions',
      rating: 4,
      comments: 'Interested in cloud infrastructure solutions but need more technical details.',
      createdAt: '2024-01-04T12:00:00Z',
      updatedAt: '2024-01-04T12:00:00Z',
    },
  ];

  const mockStats: DemoFeedbackStats = {
    totalFeedbacks: 4,
    averageRating: 4.5,
    topicBreakdown: [
      { topic: 'Mobile Solutions', count: 1, percentage: 25 },
      { topic: 'MAS Migration', count: 1, percentage: 25 },
      { topic: 'Support Extension', count: 1, percentage: 25 },
      { topic: 'Other', count: 1, percentage: 25 },
    ],
    ratingDistribution: [
      { rating: 5, count: 2, percentage: 50 },
      { rating: 4, count: 2, percentage: 50 },
      { rating: 3, count: 0, percentage: 0 },
      { rating: 2, count: 0, percentage: 0 },
      { rating: 1, count: 0, percentage: 0 },
    ],
    contactRequestRate: 75,
    recentFeedbacks: 4,
  };

  const fetchFeedbacks = async () => {
    setLoading(true);
    try {
      const params: DemoFeedbackListParams = {
        page: currentPage,
        limit: pageSize,
        search: searchText || undefined,
        rating: selectedRating,
        startDate: dateRange[0]?.toISOString(),
        endDate: dateRange[1]?.toISOString(),
      };

      try {
        const response = await demoFeedbackApi.getFeedbackList(params);
        setFeedbacks(response.feedbacks);
        setTotal(response.total);
      } catch (apiError) {
        // Fallback to mock data if API fails
        console.warn('API unavailable, using mock data:', apiError);
        let filteredMockData = [...mockFeedbacks];
        
        // Apply filters to mock data
        if (searchText) {
          filteredMockData = filteredMockData.filter(feedback =>
            feedback.name.toLowerCase().includes(searchText.toLowerCase()) ||
            feedback.email.toLowerCase().includes(searchText.toLowerCase()) ||
            feedback.organization?.toLowerCase().includes(searchText.toLowerCase()) ||
            feedback.comments?.toLowerCase().includes(searchText.toLowerCase())
          );
        }
        
        if (selectedRating) {
          filteredMockData = filteredMockData.filter(feedback => feedback.rating === selectedRating);
        }
        
        setFeedbacks(filteredMockData);
        setTotal(filteredMockData.length);
      }
    } catch (error) {
      console.error('Failed to fetch feedback:', error);
      message.error('Failed to load feedback data');
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      try {
        const stats = await demoFeedbackApi.getFeedbackStats();
        setStatistics(stats);
      } catch (apiError) {
        // Fallback to mock stats if API fails
        console.warn('Statistics API unavailable, using mock data:', apiError);
        setStatistics(mockStats);
      }
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
    }
  };

  const handleRefresh = () => {
    fetchFeedbacks();
    fetchStatistics();
  };

  const handleExport = async () => {
    try {
      const params: DemoFeedbackListParams = {
        search: searchText || undefined,
        rating: selectedRating,
        startDate: dateRange[0]?.toISOString(),
        endDate: dateRange[1]?.toISOString(),
      };

      const blob = await demoFeedbackApi.exportFeedback(params);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `demo-feedback-${dayjs().format('YYYY-MM-DD')}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      message.success('Feedback data exported successfully');
    } catch (error) {
      console.error('Failed to export feedback:', error);
      message.error('Failed to export feedback data');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await demoFeedbackApi.deleteFeedback(id);
      message.success('Feedback deleted successfully');
      fetchFeedbacks();
      fetchStatistics();
    } catch (error) {
      console.error('Failed to delete feedback:', error);
      message.error('Failed to delete feedback');
    }
  };

  const showFeedbackDetails = (feedback: SimpleFeedback) => {
    setSelectedFeedback(feedback);
    setDetailModalVisible(true);
  };

  const columns: ColumnsType<SimpleFeedback> = [
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      sorter: (a, b) => dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix(),
      defaultSortOrder: 'descend',
      render: (createdAt) => (
        <Tooltip title={dayjs(createdAt).format('YYYY-MM-DD HH:mm:ss')}>
          <Text>{dayjs(createdAt).format('MMM DD')}</Text>
        </Tooltip>
      ),
    },
    {
      title: 'Contact Info',
      key: 'contact',
      width: 200,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.name}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>{record.email}</Text>
          {record.phone && (
            <Text type="secondary" style={{ fontSize: '11px' }}>{record.phone}</Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Organization',
      dataIndex: 'organization',
      key: 'organization',
      width: 150,
      ellipsis: true,
      render: (organization) => organization ? (
        <Tooltip title={organization}>
          <Text>{organization}</Text>
        </Tooltip>
      ) : (
        <Text type="secondary">Not specified</Text>
      ),
    },
    {
      title: 'Rating',
      dataIndex: 'rating',
      key: 'rating',
      width: 120,
      sorter: (a, b) => a.rating - b.rating,
      filters: [
        { text: '5 Stars', value: 5 },
        { text: '4 Stars', value: 4 },
        { text: '3 Stars', value: 3 },
        { text: '2 Stars', value: 2 },
        { text: '1 Star', value: 1 },
      ],
      onFilter: (value, record) => record.rating === value,
      render: (rating) => (
        <Space>
          <Rate disabled value={rating} style={{ fontSize: '14px' }} />
          <Text>({rating}/5)</Text>
        </Space>
      ),
    },
    {
      title: 'Details',
      dataIndex: 'comments',
      key: 'comments',
      ellipsis: true,
      render: (comments) => {
        if (!comments) return <Text type="secondary">No details</Text>;
        
        // Parse the structured comments from demo feedback
        const parts = comments.split(' | ');
        const topic = parts.find((p: string) => p.startsWith('Topic:'))?.replace('Topic: ', '') || '';
        const timeline = parts.find((p: string) => p.startsWith('Timeline:'))?.replace('Timeline: ', '') || '';
        const followUp = parts.find((p: string) => p.startsWith('Follow-up:'))?.replace('Follow-up: ', '') || '';
        const wantsDiscussion = parts.includes('Wants follow-up discussion');
        const additionalComments = parts.find((p: string) => p.startsWith('Comments:'))?.replace('Comments: ', '') || '';
        
        return (
          <Tooltip title={comments}>
            <Space direction="vertical" size={0}>
              {topic && <Text style={{ fontSize: '12px' }}><strong>Topic:</strong> {topic}</Text>}
              {timeline && <Text style={{ fontSize: '11px', color: '#666' }}>Timeline: {timeline}</Text>}
              {followUp && <Text style={{ fontSize: '11px', color: '#1890ff' }}>Follow-up: {followUp}</Text>}
              {wantsDiscussion && <Text style={{ fontSize: '11px', color: '#52c41a' }}>✓ Wants discussion</Text>}
              {additionalComments && (
                <Text style={{ fontSize: '11px', fontStyle: 'italic' }} ellipsis>
                  "{additionalComments.substring(0, 50)}{additionalComments.length > 50 ? '...' : ''}"
                </Text>
              )}
            </Space>
          </Tooltip>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => showFeedbackDetails(record)}
            size="small"
          />
          <Popconfirm
            title="Delete this feedback?"
            description="This action cannot be undone."
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Button
              type="text"
              icon={<DeleteOutlined />}
              danger
              size="small"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <Space align="center" style={{ marginBottom: '16px' }}>
          <Title level={2} style={{ margin: 0 }}>Demo Feedback</Title>
          <Button
            icon={<DownloadOutlined />}
            onClick={handleExport}
          >
            Export
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
            loading={loading}
          >
            Refresh
          </Button>
        </Space>

        {/* Statistics Cards */}
        {statistics && (
          <Row gutter={16} style={{ marginBottom: '16px' }}>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Total Feedback"
                  value={statistics.totalFeedbacks}
                  prefix={<BarChartOutlined />}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Average Rating"
                  value={statistics.averageRating}
                  precision={1}
                  prefix={<StarOutlined />}
                  suffix="/ 5"
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Contact Requests"
                  value={statistics.contactRequestRate}
                  precision={1}
                  suffix="%"
                  prefix={<ContactsOutlined />}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Recent (7 days)"
                  value={statistics.recentFeedbacks}
                  prefix={<CalendarOutlined />}
                />
              </Card>
            </Col>
          </Row>
        )}
        
        {statistics && statistics.topicBreakdown && statistics.topicBreakdown.length > 0 && (
          <Row gutter={16} style={{ marginBottom: '16px' }}>
            <Col span={24}>
              <Card title="Topic Breakdown" size="small">
                <Row gutter={8}>
                  {statistics.topicBreakdown.map((topic, index) => (
                    <Col span={6} key={index}>
                      <Statistic
                        title={topic.topic}
                        value={topic.count}
                        suffix={`(${topic.percentage}%)`}
                        valueStyle={{ fontSize: '16px' }}
                      />
                    </Col>
                  ))}
                </Row>
              </Card>
            </Col>
          </Row>
        )}

        {/* Filters */}
        <Card size="small" style={{ marginBottom: '16px' }}>
          <Row gutter={[16, 16]} align="middle">
            <Col span={8}>
              <Input
                placeholder="Search by name, email, organization, or comments..."
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                allowClear
              />
            </Col>
            <Col span={3}>
              <Select
                placeholder="Rating"
                value={selectedRating}
                onChange={setSelectedRating}
                allowClear
                style={{ width: '100%' }}
              >
                <Option value={5}>5 Stars</Option>
                <Option value={4}>4 Stars</Option>
                <Option value={3}>3 Stars</Option>
                <Option value={2}>2 Stars</Option>
                <Option value={1}>1 Star</Option>
              </Select>
            </Col>
            <Col span={3}>
              <Select
                placeholder="Topic"
                allowClear
                style={{ width: '100%' }}
                onChange={(value) => {
                  // This would be used if we implement topic filtering
                  console.log('Topic filter:', value);
                }}
              >
                <Option value="Mobile Solutions">Mobile Solutions</Option>
                <Option value="MAS Migration">MAS Migration</Option>
                <Option value="Support Extension">Support Extension</Option>
                <Option value="Other">Other</Option>
              </Select>
            </Col>
            <Col span={10}>
              <RangePicker
                value={dateRange}
                onChange={(dates) => setDateRange(dates as [Dayjs | null, Dayjs | null])}
                style={{ width: '100%' }}
                placeholder={['Start Date', 'End Date']}
              />
            </Col>
          </Row>
        </Card>
      </div>

      {/* Main Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={feedbacks}
          rowKey="id"
          loading={loading}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            onChange: (page, size) => {
              setCurrentPage(page);
              setPageSize(size || 20);
            },
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* Detail Modal */}
      <Modal
        title="Feedback Details"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={null}
        width={700}
      >
        {selectedFeedback && (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Descriptions title="Contact Information" bordered>
              <Descriptions.Item label="Name">{selectedFeedback.name}</Descriptions.Item>
              <Descriptions.Item label="Email">{selectedFeedback.email}</Descriptions.Item>
              <Descriptions.Item label="Phone">{selectedFeedback.phone || 'Not provided'}</Descriptions.Item>
              <Descriptions.Item label="Organization">{selectedFeedback.organization || 'Not specified'}</Descriptions.Item>
              <Descriptions.Item label="Submitted">
                {dayjs(selectedFeedback.createdAt).format('YYYY-MM-DD HH:mm:ss')}
              </Descriptions.Item>
              <Descriptions.Item label="Rating">
                <Space>
                  <Rate disabled value={selectedFeedback.rating} />
                  <Text>({selectedFeedback.rating}/5)</Text>
                </Space>
              </Descriptions.Item>
            </Descriptions>

            {selectedFeedback.comments && (
              <div>
                <Title level={5}>Feedback Details</Title>
                <Card size="small">
                  {(() => {
                    const parts = selectedFeedback.comments.split(' | ');
                    const topic = parts.find((p: string) => p.startsWith('Topic:'))?.replace('Topic: ', '') || '';
                    const timeline = parts.find((p: string) => p.startsWith('Timeline:'))?.replace('Timeline: ', '') || '';
                    const followUp = parts.find((p: string) => p.startsWith('Follow-up:'))?.replace('Follow-up: ', '') || '';
                    const wantsDiscussion = parts.includes('Wants follow-up discussion');
                    const additionalComments = parts.find((p: string) => p.startsWith('Comments:'))?.replace('Comments: ', '') || '';
                    
                    return (
                      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                        {topic && (
                          <div>
                            <Text strong>Demo Topic: </Text>
                            <Text>{topic}</Text>
                          </div>
                        )}
                        {timeline && (
                          <div>
                            <Text strong>Implementation Timeline: </Text>
                            <Text>{timeline}</Text>
                          </div>
                        )}
                        {followUp && (
                          <div>
                            <Text strong>Follow-up Interests: </Text>
                            <Text>{followUp}</Text>
                          </div>
                        )}
                        {wantsDiscussion && (
                          <div>
                            <Text strong>Contact Preference: </Text>
                            <Text style={{ color: '#52c41a' }}>✓ Wants follow-up discussion</Text>
                          </div>
                        )}
                        {additionalComments && (
                          <div>
                            <Text strong>Additional Comments: </Text>
                            <Paragraph style={{ marginTop: '8px', fontStyle: 'italic', backgroundColor: '#f5f5f5', padding: '12px', borderRadius: '6px' }}>
                              "{additionalComments}"
                            </Paragraph>
                          </div>
                        )}
                        {!topic && !timeline && !followUp && !wantsDiscussion && !additionalComments && (
                          <Paragraph>{selectedFeedback.comments}</Paragraph>
                        )}
                      </Space>
                    );
                  })()
                  }
                </Card>
              </div>
            )}
          </Space>
        )}
      </Modal>
    </div>
  );
};

export default DemoFeedbackPage;